import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Download } from "lucide-react";
import { api, errMsg, downloadBlob } from "../../lib/api";

export const RECORD_STATUSES = {
  criteria: ["New", "Contacted", "Searching", "Active", "Closed"],
  requests: ["Pending", "Approved", "Declined"],
  sellers: ["New", "Reviewing", "Matched", "Not a fit", "Closed"],
  inquiries: ["New", "Contacted", "Closed"],
};

const TITLE = {
  criteria: (r) => [r.full_name, `${(r.property_types || []).join(", ") || "Any type"} · ${(r.locations || []).join(", ") || "Any market"}`],
  requests: (r) => [r.name, `${r.property_title} · ${r.property_city}`],
  sellers: (r) => [r.owner_name, `${r.property_type} · ${r.address}, ${r.city}`],
  inquiries: (r) => [r.name, r.inquiry_type],
};
const HIDE = new Set(["id", "notes_log", "files", "user_id", "property_id", "status"]);

function Row({ kind, r, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [title, sub] = TITLE[kind](r);
  const update = async (body) => {
    try {
      const { data } = await api.patch(`/admin/records/${kind}/${r.id}`, body);
      onUpdated(data);
      toast.success("Updated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="bg-white" data-testid={`record-${kind}-${r.id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4">
        <button onClick={() => setOpen(!open)} className="flex-1 text-left flex items-center gap-3" data-testid={`record-toggle-${r.id}`}>
          <ChevronDown className={`h-4 w-4 text-mist transition-transform ${open ? "rotate-180" : ""}`} />
          <span><span className="font-semibold text-navy">{title}</span><span className="block text-xs text-mist">{sub} · {r.email} · {new Date(r.created_at).toLocaleDateString()}</span></span>
        </button>
        <select className="field !py-2 !w-auto text-sm" value={r.status} onChange={(e) => update({ status: e.target.value })} data-testid={`record-status-${r.id}`}>
          {RECORD_STATUSES[kind].map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      {open && (
        <div className="border-t border-navy/10 p-4 sm:p-6 space-y-5 bg-paper/60">
          <dl className="grid gap-3 sm:grid-cols-3 text-sm">
            {Object.entries(r).filter(([k, v]) => !HIDE.has(k) && v !== "" && v != null && !(Array.isArray(v) && !v.length)).map(([k, v]) => (
              <div key={k}><dt className="text-[10px] uppercase tracking-wider text-mist">{k.replace(/_/g, " ")}</dt><dd className="text-navy break-words">{Array.isArray(v) ? v.join(", ") : String(v)}</dd></div>
            ))}
          </dl>
          {r.files?.length > 0 && (
            <div className="flex flex-wrap gap-2">{r.files.map((x) => (
              <button key={x.id} onClick={() => downloadBlob(`/admin/files/${x.id}`, x.original_filename).catch((e) => toast.error(errMsg(e)))} className="btn-ghost text-navy !py-2 text-xs" data-testid={`record-file-${x.id}`}><Download className="h-3.5 w-3.5" />{x.original_filename}</button>
            ))}</div>
          )}
          <div>
            <p className="label">Communications log</p>
            <ul className="space-y-2 text-sm">{(r.notes_log || []).map((n, i) => <li key={i} className="border-l-2 border-gold pl-3"><span className="text-navy">{n.text}</span><span className="block text-xs text-mist">{n.by} · {new Date(n.at).toLocaleString()}</span></li>)}</ul>
            <div className="mt-3 flex gap-2">
              <input className="field !py-2 text-sm" placeholder="Log a call, email or note…" value={note} onChange={(e) => setNote(e.target.value)} data-testid={`record-note-input-${r.id}`} />
              <button onClick={() => { if (note.trim()) { update({ note }); setNote(""); } }} className="btn-primary !py-2" data-testid={`record-note-add-${r.id}`}>Add</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Records({ kind }) {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get(`/admin/records/${kind}`).then((r) => setItems(r.data)); }, [kind]);
  if (!items) return <p className="text-mist">Loading…</p>;
  if (!items.length) return <p className="border border-dashed border-navy/20 p-10 text-center text-mist" data-testid={`records-empty-${kind}`}>Nothing here yet.</p>;
  return (
    <div className="border border-navy/10 divide-y divide-navy/10" data-testid={`records-${kind}`}>
      {items.map((r) => <Row key={r.id} kind={kind} r={r} onUpdated={(n) => setItems(items.map((x) => (x.id === n.id ? n : x)))} />)}
    </div>
  );
}
